import { useEffect, useRef } from 'react';
import { trackAbandonment } from '../telemetry/telemetryEvents';
import { logDev as debugLog } from '@/logging/devConsole';

/**
 * Hook to track user abandonment scenarios
 * Monitors browser events to detect when users leave the wizard
 */
export const useAbandonmentTracking = () => {
  const isUnloadingRef = useRef(false);
  const isAuthRedirectingRef = useRef(false);

  useEffect(() => {
    // Listen for auth redirect state changes
    const handleAuthRedirectChange = (event: CustomEvent) => {
      isAuthRedirectingRef.current = event.detail.started;
      debugLog('[AbandonmentTracking] Auth redirect state changed:', event.detail.started);
    };
    
    window.addEventListener('authRedirectStateChange', handleAuthRedirectChange as EventListener);
      
    // Track before page unload (browser close or refresh)
    const handleBeforeUnload = () => {
      // Don't track abandonment if scenario was already completed successfully
      // This flag is set in AppDownloadScreen after completeScenario('success')
      if (sessionStorage.getItem('scenarioCompleted') === 'true') {
        debugLog('[AbandonmentTracking] Ignoring beforeunload - scenario already completed');
        return;
      }

      // Check both ref and sessionStorage for auth redirect state (ref for current session, storage for persisted state)
      const authRedirectInProgress = isAuthRedirectingRef.current || 
        sessionStorage.getItem('authRedirectInProgress') === 'true';
      
      if (authRedirectInProgress) {
        debugLog('[AbandonmentTracking] Ignoring beforeunload - auth redirect in progress');
        return;
      }
      
      isUnloadingRef.current = true;

      // Track abandonment for browser close/refresh
      trackAbandonment('browser-close');
    };
    
    // Track navigation away from the application
    const handleUnload = () => {
      // Don't track abandonment if scenario was already completed successfully
      if (sessionStorage.getItem('scenarioCompleted') === 'true') {
        debugLog('[AbandonmentTracking] Ignoring unload - scenario already completed');
        return;
      }

      // Check both ref and sessionStorage for auth redirect state
      const authRedirectInProgress = isAuthRedirectingRef.current || 
        sessionStorage.getItem('authRedirectInProgress') === 'true';
      
      if (authRedirectInProgress) {
        debugLog('[AbandonmentTracking] Ignoring unload - auth redirect in progress');
        return;
      }
      
      isUnloadingRef.current = true;
      trackAbandonment('navigate-away');
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    // Cleanup function
    return () => {
      // Remove event listeners
      window.removeEventListener('authRedirectStateChange', handleAuthRedirectChange as EventListener);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  // Function to manually track user dropout (for UI interactions)
  const trackUserDropout = () => {
    trackAbandonment('user-dropout');
  };

  return { trackUserDropout };
};